/**
 * Shared responsive content column used by the Anear header and every surface ABR draws.
 *
 * - phones: viewport minus a 4px side bezel (so rounded header/EventFrame cards are not flush)
 * - sm and up: 600px (never shrink as the viewport grows)
 *
 * Nothing ABR renders — pages, ABR overlays, dialogs, drawers, camera viewfinders — may be
 * wider than the Anear header column. Use the helpers below instead of re-deriving the caps:
 *
 * - `contentColumnShellSx` + `contentColumnMaxWidthSx`: in-flow page/overlay content
 * - `contentColumnPageShellSx`: native ABR pages (same width as the header; vertical gap only)
 * - `eventFrameColumnSx`: AppM HTML frame (same width as the header; 9:16 height only)
 * - `contentColumnOverlaySx`: anything `position: fixed` against the viewport
 * - `CONTENT_COLUMN_MAX_WIDTH_VAR_CSS`: plain DOM (body portals, shadow DOM, injected CSS)
 * - `contentColumnAnchorSx` / `contentColumnBackdropSx`: panels pinned to a column edge
 */
export const CONTENT_COLUMN_GUTTER_PX = 4

export const CONTENT_COLUMN_MAX_WIDTH = {
  xs: `calc(100% - ${CONTENT_COLUMN_GUTTER_PX * 2}px)`,
  sm: 600,
  md: 600
}

/** CSS custom-property friendly values (units required for numeric breakpoints). */
export const CONTENT_COLUMN_MAX_WIDTH_CSS = {
  xs: CONTENT_COLUMN_MAX_WIDTH.xs,
  sm: `${CONTENT_COLUMN_MAX_WIDTH.sm}px`,
  md: `${CONTENT_COLUMN_MAX_WIDTH.md}px`
}

/**
 * Published on `:root` by src/index.css so non-MUI code (portals mounted on document.body,
 * shadow DOM app content, imperative styles) can honor the cap without a theme.
 */
export const CONTENT_COLUMN_MAX_WIDTH_VAR = '--anear-content-column-max'
export const CONTENT_COLUMN_MAX_WIDTH_VAR_CSS = `var(${CONTENT_COLUMN_MAX_WIDTH_VAR}, 100%)`

export const contentColumnShellSx = {
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  boxSizing: 'border-box',
  maxWidth: '100%'
}

export const contentColumnMaxWidthSx = {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  maxWidth: CONTENT_COLUMN_MAX_WIDTH
}

/**
 * In-flow ABR page wrapper. Same width as the header — no extra horizontal gutter
 * beyond the shared column cap. Vertical padding separates the page from the header.
 */
export const contentColumnPageShellSx = {
  ...contentColumnShellSx,
  overflowX: 'hidden',
  pt: 0.5,
  pb: 0.5
}

/**
 * EventFrame shell: same width as the Anear header at every breakpoint.
 *
 * Width is the header column only. Do not fold `100svh` into `width` — Chrome
 * device-frame swaps change height independently of width and would desync the
 * frame from the header. Height may follow 9:16 and clamp to the viewport;
 * `minWidth` uses the :root var so aspect-ratio + max-height cannot transfer a
 * smaller used width. Injected app HTML (dangerouslySetInnerHTML) then fills
 * 100% of this box; `@media` in that CSS still sees the viewport, so apps
 * should prefer `@container` / `cqw` against this inline-size container.
 */
export const eventFrameColumnSx = {
  ...contentColumnMaxWidthSx,
  position: 'relative',
  minWidth: CONTENT_COLUMN_MAX_WIDTH_VAR_CSS,
  aspectRatio: '9 / 16',
  maxHeight: 'calc(100svh - 4vh)',
  overflow: 'hidden',
  borderRadius: 2,
  containerType: 'inline-size'
}

/**
 * Centers a viewport-anchored (`position: fixed`) overlay inside the header column.
 *
 * Auto inline margins rather than a translate keeps the element from becoming a containing
 * block for `position: fixed` descendants, and sidesteps 100vw/scrollbar rounding. Values are
 * strings so the same object works for `sx`, React inline styles, and `element.style`.
 */
export const contentColumnOverlaySx = {
  left: '0',
  right: '0',
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: CONTENT_COLUMN_MAX_WIDTH_VAR_CSS
}

/** Pins a fixed panel (e.g. the burger menu) to the left edge of the column. */
export const contentColumnAnchorSx = theme => ({
  [theme.breakpoints.up('sm')]: {
    left: `calc((100vw - ${CONTENT_COLUMN_MAX_WIDTH.sm}px) / 2)`,
    right: 'auto'
  },
  [theme.breakpoints.up('md')]: {
    left: `calc((100vw - ${CONTENT_COLUMN_MAX_WIDTH.md}px) / 2)`,
    right: 'auto'
  }
})

/** Full-height scrim that dims the column only, never the surrounding page. */
export const contentColumnBackdropSx = theme => ({
  position: 'fixed',
  top: 0,
  bottom: 0,
  left: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    ...contentColumnAnchorSx(theme)[theme.breakpoints.up('sm')],
    width: CONTENT_COLUMN_MAX_WIDTH.sm
  },
  [theme.breakpoints.up('md')]: {
    ...contentColumnAnchorSx(theme)[theme.breakpoints.up('md')],
    width: CONTENT_COLUMN_MAX_WIDTH.md
  }
})
